import { Building2, RefreshCw, Users, Megaphone, History, Shield, type LucideIcon } from "lucide-react";

/**
 * Onglets de la fiche location — identités, ordre, et rattachement des champs.
 *
 * Fichier séparé du composant `FicheTabBar` pour ne pas casser le Fast Refresh
 * (un module qui exporte à la fois des composants et des constantes le perd).
 *
 * Critère de découpe : **temporel**, pas thématique. Un champ qui survit au
 * départ du locataire va dans « Le bien » ; les autres suivent le cycle.
 */
export type FicheTabId =
  | "bien"
  | "cycle"
  | "locataires"
  | "commercialisation"
  | "historique"
  | "permissions";

/**
 * Les 4 onglets métier, dans l'ordre de la spec.
 *
 * STRUCTURE CONSTANTE — exigence produit : ils sont TOUJOURS présents, même
 * vides. « Cycle en cours » affiche un état vide plutôt que de disparaître,
 * pour que le C-Level retrouve la même fiche à chaque ouverture. `permissions`
 * est le seul onglet conditionnel (admin) : ce n'est pas la fiche, c'est la
 * matrice des droits.
 *
 * `historique` est volontairement absent de cette liste — masqué pour tous
 * les rôles (décision Thibaud 2026-08-20 : c'est un stub non branché sur
 * `srs_historique`, cf. son bloc de rendu dans BienDetailPage, qui n'affiche
 * jamais qu'un message statique). Le type `FicheTabId`, `FICHE_TAB_META` et
 * le rendu restent en place pour la passe dédiée qui le branchera un jour —
 * il suffira de le rajouter ici.
 */
export const FICHE_TAB_IDS: FicheTabId[] = [
  "bien",
  "cycle",
  "locataires",
  "commercialisation",
];

/** Libellé et icône de chaque onglet de la fiche LOCATION. */
export const FICHE_TAB_META: Record<FicheTabId, { labelKey: string; icon: LucideIcon }> = {
  bien: { labelKey: "biens.ficheTabs.bien", icon: Building2 },
  cycle: { labelKey: "biens.ficheTabs.cycle", icon: RefreshCw },
  locataires: { labelKey: "biens.ficheTabs.locataires", icon: Users },
  commercialisation: { labelKey: "biens.ficheTabs.commercialisation", icon: Megaphone },
  historique: { labelKey: "biens.ficheTabs.historique", icon: History },
  permissions: { labelKey: "biens.droitsDesChamps", icon: Shield },
};

/**
 * Dans quel onglet se corrige chaque champ signalé par les bannières de
 * complétude (`lib/bienPrepValidation`).
 *
 * Sans cette table, les bannières listent jusqu'à treize champs manquants sans
 * jamais dire OÙ les remplir : « Renon reçu le » et « Expert PEB » vivent dans
 * « Cycle en cours », rien ne l'indiquait. Les pastilles des onglets s'en
 * servent pour porter le compte au bon endroit.
 */
const TAB_OF_FIELD: Record<string, FicheTabId> = {
  // Onglet « Le bien »
  referenceInterne: "bien", projet: "bien", typeBien: "bien", transaction: "bien",
  adresse: "bien", ville: "bien", etage: "bien",
  companyContract: "bien", meuble: "bien",
  nbChambres: "bien", nbSdb: "bien", typeSol: "bien",
  caveDisponible: "bien", caveNumero: "bien",
  parkingDisponible: "bien", parkingNumero: "bien", parkingLoyerMensuel: "bien",
  garageDisponible: "bien",
  chargesMensuelles: "bien", loyerMensuel: "bien", chargesIncluses: "bien",
  classePeb: "bien", pebFinLe: "bien", consoKwh: "bien",
  // Onglet « Cycle en cours »
  seraDispoPour: "cycle", renonRecuLe: "cycle", finDeContrat: "cycle", cycleOuvertLe: "cycle",
  collaborateurSrs: "cycle", expertPeb: "cycle", prestataireEle: "cycle",
};

/** Compte les champs manquants par onglet, à partir des listes de validation. */
export function countMissingByTab(missing: string[]): Partial<Record<FicheTabId, number>> {
  const out: Partial<Record<FicheTabId, number>> = {};
  for (const key of missing) {
    const tab = TAB_OF_FIELD[key];
    if (!tab) continue;
    out[tab] = (out[tab] ?? 0) + 1;
  }
  return out;
}
